import { useState, useEffect } from "react";
import { Plus, Trash2, Video } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminHomepageMedia = () => {
  const { token } = useAuth();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedSection, setSelectedSection] = useState("hero");

  useEffect(() => {
    fetchMedia();
  }, [token]);

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${API}/homepage-media`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMedia(response.data);
    } catch (error) {
      toast.error("Failed to fetch media");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append("file", file);
      formData.append("section", selectedSection);

      try {
        await axios.post(`${API}/homepage-media`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    toast.success("Media uploaded successfully");
    setUploading(false);
    fetchMedia();
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm("Delete this media?")) return;

    try {
      await axios.delete(`${API}/homepage-media/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Media deleted");
      fetchMedia();
    } catch (error) {
      toast.error("Failed to delete media");
    }
  };

  const getMediaUrl = (mediaPath) => {
    if (!mediaPath) return null;
    if (mediaPath.startsWith("http")) return mediaPath;
    return `${BACKEND_URL}${mediaPath}`;
  };

  const heroMedia = media.filter((m) => m.section === "hero");
  const bannerMedia = media.filter((m) => m.section === "banner");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-2 border-royal-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="admin-homepage-media-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <p className="font-display text-xs tracking-[0.3em] uppercase text-royal-gold mb-2">Visuals</p>
          <h1 className="font-serif text-3xl text-cream">Homepage Media</h1>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={selectedSection} onValueChange={setSelectedSection}>
            <SelectTrigger className="w-32 bg-velvet border-royal-gold/20 text-cream" data-testid="section-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-charcoal border-royal-gold/20">
              <SelectItem value="hero" className="text-cream">Hero</SelectItem>
              <SelectItem value="banner" className="text-cream">Banner</SelectItem>
            </SelectContent>
          </Select>
          
          <label>
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              className="hidden"
              onChange={handleUpload}
              disabled={uploading}
            />
            <span 
              className="btn-luxury flex items-center space-x-2 cursor-pointer py-3"
              data-testid="upload-media-btn"
            >
              <Plus size={18} />
              <span>{uploading ? "Uploading..." : "Upload"}</span>
            </span>
          </label>
        </div>
      </div>

      {/* Hero Section */}
      <div className="mb-12">
        <div className="flex items-center mb-6">
          <span className="w-2 h-2 bg-royal-gold mr-3"></span>
          <h2 className="font-serif text-xl text-cream">Hero Section</h2>
        </div>
        <p className="font-body text-sm text-cream/50 mb-6">
          Hero images/videos appear in the main banner on the homepage with auto-rotation.
        </p>
        
        {heroMedia.length === 0 ? (
          <div className="bg-charcoal/30 border border-dashed border-royal-gold/20 p-12 text-center">
            <p className="font-body text-sm text-cream/50">No hero media yet</p>
            <p className="font-body text-xs text-cream/30 mt-1">
              Select "Hero" and upload images or videos
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {heroMedia.map((item) => (
              <div
                key={item.id}
                className="relative aspect-video bg-velvet overflow-hidden group border border-royal-gold/10"
              >
                {item.media_type === "video" ? (
                  <video
                    src={getMediaUrl(item.media_path)}
                    className="w-full h-full object-cover"
                    muted
                  />
                ) : (
                  <img
                    src={getMediaUrl(item.media_path)}
                    alt="Hero"
                    className="w-full h-full object-cover"
                  />
                )}
                
                <div className="absolute inset-0 bg-deep-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <button
                    onClick={() => handleDelete(item.id)}
                    data-testid={`delete-hero-${item.id}`}
                    className="w-10 h-10 bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                {item.media_type === "video" && (
                  <div className="absolute bottom-2 left-2 bg-deep-black/80 text-cream text-xs px-2 py-1 flex items-center space-x-1">
                    <Video size={12} />
                    <span>Video</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Banner Section */}
      <div>
        <div className="flex items-center mb-6">
          <span className="w-2 h-2 bg-silver mr-3"></span>
          <h2 className="font-serif text-xl text-cream">Banner Section</h2>
        </div>
        <p className="font-body text-sm text-cream/50 mb-6">
          Banner images for promotional content or additional showcases.
        </p>
        
        {bannerMedia.length === 0 ? (
          <div className="bg-charcoal/30 border border-dashed border-royal-gold/20 p-12 text-center">
            <p className="font-body text-sm text-cream/50">No banner media yet</p>
            <p className="font-body text-xs text-cream/30 mt-1">
              Select "Banner" and upload images or videos
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {bannerMedia.map((item) => (
              <div
                key={item.id}
                className="relative aspect-video bg-velvet overflow-hidden group border border-silver/10"
              >
                {item.media_type === "video" ? (
                  <video
                    src={getMediaUrl(item.media_path)}
                    className="w-full h-full object-cover"
                    muted
                  />
                ) : (
                  <img
                    src={getMediaUrl(item.media_path)}
                    alt="Banner"
                    className="w-full h-full object-cover"
                  />
                )}
                
                <div className="absolute inset-0 bg-deep-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <button
                    onClick={() => handleDelete(item.id)}
                    data-testid={`delete-banner-${item.id}`}
                    className="w-10 h-10 bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                {item.media_type === "video" && (
                  <div className="absolute bottom-2 left-2 bg-deep-black/80 text-cream text-xs px-2 py-1 flex items-center space-x-1">
                    <Video size={12} />
                    <span>Video</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Guidelines */}
      <div className="mt-12 p-6 bg-charcoal/30 border border-royal-gold/10">
        <h3 className="font-serif text-lg text-cream mb-4">Media Guidelines</h3>
        <ul className="space-y-2 font-body text-sm text-cream/60">
          <li className="flex items-start space-x-3">
            <span className="w-1 h-1 bg-royal-gold mt-2"></span>
            <span>Recommended: 1920x1080 pixels for optimal display</span>
          </li>
          <li className="flex items-start space-x-3">
            <span className="w-1 h-1 bg-royal-gold mt-2"></span>
            <span>Supported: JPG, PNG, WEBP for images; MP4, WEBM for videos</span>
          </li>
          <li className="flex items-start space-x-3">
            <span className="w-1 h-1 bg-royal-gold mt-2"></span>
            <span>Keep videos under 50MB for faster loading</span>
          </li>
          <li className="flex items-start space-x-3">
            <span className="w-1 h-1 bg-royal-gold mt-2"></span>
            <span>Hero media auto-rotates every 6 seconds</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default AdminHomepageMedia;
